import { Box, Skeleton, Typography } from "@mui/material";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { ActivityTableSkeleton } from "src/components/ui/ActivityTableSkeleton";

export const QualityEventRecordSkeleton = () => (
  <Box
    className="flex flex-col gap-4"
    aria-label="Loading record"
    aria-busy="true"
    sx={{
      "& .MuiSkeleton-root": { fontSize: "inherit", lineHeight: "inherit" },
      "& .MuiSkeleton-text": { transform: "scale(1, 0.6)" },
    }}
  >
    <PageHeader
      pageTitleComponent={
        <PageBreadcrumb
          items={[
            { label: "Quality Event Management" },
            { label: <Skeleton width={40} /> },
            { label: <Skeleton sx={{ width: { xs: 60, sm: 240 } }} /> },
          ]}
        />
      }
      rightButtons={<Skeleton variant="rounded" width={115} height={30} />}
    />
    <div className="flex flex-col gap-4 px-3 py-2" aria-hidden="true">
      <section>
        <dl className="m-0 grid grid-cols-1 gap-4 min-[900px]:my-4 min-[900px]:grid-cols-4">
          {Array.from({ length: 7 }, (_, index) => (
            <div key={index} className="min-w-0">
              <Typography
                variant="overline"
                component="dt"
                className="leading-[1.2]"
              >
                <Skeleton width={65} />
              </Typography>
              <Typography
                component="dd"
                variant={index < 5 ? "body2" : "body1"}
                className="m-0 mt-1"
              >
                <Skeleton width="60%" />
              </Typography>
            </div>
          ))}
        </dl>
      </section>
      <section className="flex flex-col gap-2">
        <Typography variant="subtitle1">
          <Skeleton width={130} />
        </Typography>
        <div className="flex flex-col divide-y divide-solid divide-slate-200 overflow-hidden rounded-md border border-solid border-slate-200">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index}>
              <div className="flex flex-wrap items-center gap-x-3 px-[10.5px] py-[7px]">
                <div className="flex min-w-0 flex-1 basis-48 items-center gap-3">
                  <div className="flex h-8 items-center">
                    <Skeleton variant="rounded" width={17.5} height={17.5} />
                  </div>
                  <Typography variant="body1" className="w-36 max-w-full">
                    <Skeleton />
                  </Typography>
                </div>
                <div className="ml-auto flex items-center gap-3">
                  {index === 0 && (
                    <Skeleton variant="rounded" width={88} height={33} />
                  )}
                  <Skeleton variant="circular" width={26} height={26} />
                </div>
              </div>
              {index === 0 && (
                <div className="bg-white px-[10.5px] py-[7px]">
                  <div className="flex flex-col gap-4 [container-type:inline-size]">
                    <Typography variant="body1">
                      <Skeleton width="75%" />
                    </Typography>
                    <Skeleton
                      variant="rounded"
                      height={40}
                      className="w-full self-start [@container(min-width:640px)]:w-[calc((100%-0.75rem)/2)]"
                    />
                    <div className="flex flex-col gap-3">
                      <Typography variant="body1">
                        <Skeleton width={300} sx={{ maxWidth: "100%" }} />
                      </Typography>
                      <div className="flex items-center gap-3">
                        <Skeleton variant="circular" width={21} height={21} />
                        <Skeleton width={30} />
                        <Skeleton variant="circular" width={21} height={21} />
                        <Skeleton width={30} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 items-start gap-3 [@container(min-width:640px)]:grid-cols-2">
                      <Skeleton
                        variant="rounded"
                        height={40}
                        className="w-full"
                      />
                      <Skeleton
                        variant="rounded"
                        height={80}
                        className="col-span-full w-full"
                      />
                    </div>
                    {[0, 1].map((field) => (
                      <Skeleton
                        key={field}
                        variant="rounded"
                        height={90}
                        className="w-full"
                      />
                    ))}
                    <div className="grid grid-cols-1 items-start gap-3 [@container(min-width:640px)]:grid-cols-2">
                      {[0, 1, 2].map((field) => (
                        <Skeleton
                          key={field}
                          variant="rounded"
                          height={40}
                          className="w-full"
                        />
                      ))}
                    </div>
                    {[90, 56, 90].map((height, field) => (
                      <Skeleton
                        key={field}
                        variant="rounded"
                        height={height}
                        className={
                          height === 56
                            ? "w-full self-start [@container(min-width:640px)]:w-[calc((100%-0.75rem)/2)]"
                            : "w-full"
                        }
                      />
                    ))}
                    <div className="flex flex-col gap-4 rounded-md bg-color-surface px-[10.5px] py-[7px] [container-type:inline-size]">
                      <Typography variant="body2">
                        <Skeleton width={140} />
                      </Typography>
                      <div className="grid grid-cols-1 gap-3 [@container(min-width:640px)]:grid-cols-2">
                        <Skeleton
                          variant="rounded"
                          height={40}
                          className="w-full"
                        />
                        <Skeleton
                          variant="rounded"
                          height={40}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
      <section className="flex flex-col gap-2">
        <Typography variant="subtitle1">
          <Skeleton width={130} />
        </Typography>
        <ActivityTableSkeleton />
      </section>
    </div>
  </Box>
);
